import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Api from '../Api/Api.json';
import {
    VictoryChart,
    VictoryBar,
    VictoryAxis,
    VictoryLabel,
} from 'victory';

const VisualizeExerciseDataForOwner = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [patientId, setPatientId] = useState(null);
    const [showMenu, setShowMenu] = useState(false);
    
    // Exercise data state
    const [exerciseList, setExerciseList] = useState([]);
    const [selectedExercise, setSelectedExercise] = useState(null);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isLoadingList, setIsLoadingList] = useState(false);
    const [isLoadingDetails, setIsLoadingDetails] = useState(false);
    const [error, setError] = useState(null);

    // Get patient ID from location state (userData from OwnerLanding)
    useEffect(() => {
        if (location.state && location.state.userData) {
            setPatientId(location.state.userData.id || location.state.userData.di_id);
        }
    }, [location.state]);

    // Fetch exercise session list
    useEffect(() => {
        const fetchExerciseList = async () => {
            if (!patientId) return;
            
            setIsLoadingList(true);
            setError(null);
            
            try {
                const headers = { headers: { "Content-Type": "application/json" } };
                const url = Api.EXERCISE_SESSION_DATA_API;
                const requestData = {
                    task: "get_exercise_session_list",
                    patient_id: patientId
                };
                
                const response = await axios.post(url, requestData, headers);
                console.log('Exercise Session List API Response:', response.data);
                
                // Handle different response structures
                let responseData = response.data;
                if (responseData?.body) responseData = responseData.body;
                if (responseData?.data) responseData = responseData.data;
                
                if (responseData && Array.isArray(responseData)) {
                    // Map API response to expected format
                    const mappedData = responseData.map(item => {
                        // Parse timestamp - API might return it as string or timestamp
                        let timestamp;
                        if (item.timestamp) {
                            timestamp = typeof item.timestamp === 'string' 
                                ? new Date(item.timestamp) 
                                : new Date(item.timestamp * 1000); // if it's Unix timestamp
                        } else if (item.datetime) {
                            timestamp = new Date(item.datetime);
                        } else {
                            timestamp = new Date();
                        }
                        
                        return {
                            datetime: item.datetime || timestamp.toISOString(),
                            timestamp: timestamp,
                            // Store original timestamp for API call
                            originalTimestamp: item.timestamp || item.datetime
                        };
                    });
                    
                    // Sort by datetime descending (newest first)
                    const sortedData = mappedData.sort((a, b) => b.timestamp - a.timestamp);
                    setExerciseList(sortedData);
                    
                    // Set first item as selected by default
                    if (sortedData.length > 0) {
                        setSelectedExercise(sortedData[0]);
                    }
                } else {
                    setExerciseList([]);
                }
            } catch (err) {
                console.error('Error fetching exercise session list:', err);
                setError('Failed to load exercise sessions. Please try again.');
                setExerciseList([]);
            } finally {
                setIsLoadingList(false);
            }
        };
        
        fetchExerciseList();
    }, [patientId]);
    
    // Fetch exercise session details when selected
    useEffect(() => {
        const fetchExerciseDetails = async () => {
            if (!patientId || !selectedExercise || !selectedExercise.originalTimestamp) return;
            
            setIsLoadingDetails(true);
            setError(null);
            
            try {
                const headers = { headers: { "Content-Type": "application/json" } };
                const url = Api.EXERCISE_SESSION_DATA_API;
                
                // Use original timestamp string as-is without timezone transformation
                // originalTimestamp is stored as a string from the API response
                const timestampForApi = typeof selectedExercise.originalTimestamp === 'string' 
                    ? selectedExercise.originalTimestamp  // Use string as-is, no conversion
                    : selectedExercise.originalTimestamp instanceof Date
                        ? selectedExercise.originalTimestamp.toISOString()  // Only convert if Date object
                        : String(selectedExercise.originalTimestamp);  // Fallback: convert to string
                
                const requestData = {
                    task: "get_exercise_session_data",
                    patient_id: patientId,
                    timestamp: timestampForApi
                };
                console.log('Exercise Session Details API Request:', requestData);                
                const response = await axios.post(url, requestData, headers);
                console.log('Exercise Session Details API Response:', response.data);
                
                // Handle different response structures
                let responseData = response.data;
                if (responseData?.body) responseData = responseData.body;
                if (responseData?.data) responseData = responseData.data;
                
                // Handle array response - take first item if array
                if (Array.isArray(responseData) && responseData.length > 0) {
                    responseData = responseData[0];
                }
                
                if (responseData) {
                    // Parse zone_summary and zone_map if they are JSON strings
                    let parsedZoneSummary = null;
                    let parsedZoneMap = null;
                    
                    if (responseData.zone_summary) {
                        if (typeof responseData.zone_summary === 'string') {
                            try {
                                parsedZoneSummary = JSON.parse(responseData.zone_summary);
                            } catch (e) {
                                console.error('Error parsing zone_summary JSON:', e);
                                parsedZoneSummary = null;
                            }
                        } else {
                            parsedZoneSummary = responseData.zone_summary;
                        }
                    }
                    
                    if (responseData.zone_map) {
                        if (typeof responseData.zone_map === 'string') {
                            try {
                                parsedZoneMap = JSON.parse(responseData.zone_map);
                            } catch (e) {
                                console.error('Error parsing zone_map JSON:', e);
                                parsedZoneMap = null;
                            }
                        } else {
                            parsedZoneMap = responseData.zone_map;
                        }
                    }
                    
                    // Merge API response with existing exercise data
                    const updatedExercise = {
                        ...selectedExercise,
                        heartrate_min: responseData.heartrate_min ?? selectedExercise.heartrate_min,
                        heartrate_max: responseData.heartrate_max ?? selectedExercise.heartrate_max,
                        heartrate_avg: responseData.heartrate_avg ?? selectedExercise.heartrate_avg,
                        total_time: responseData.total_time ?? selectedExercise.total_time,
                        total_steps: responseData.total_steps ?? selectedExercise.total_steps,
                        total_distance: responseData.total_distance ?? selectedExercise.total_distance,
                        total_calories: responseData.total_calories ?? selectedExercise.total_calories,
                        avg_speed: responseData.avg_speed ?? selectedExercise.avg_speed,
                        vo2max: responseData.vo2max ?? selectedExercise.vo2max,
                        cardio_recovery_score: responseData.cardio_recovery_score ?? responseData.cardio_recovery ?? selectedExercise.cardio_recovery_score,
                        // Store parsed zone_summary and zone_map from API response
                        zone_summary: parsedZoneSummary,
                        zone_map: parsedZoneMap
                    };
                    
                    console.log('Updated exercise with zone data:', {
                        hasZoneSummary: !!updatedExercise.zone_summary,
                        hasZoneMap: !!updatedExercise.zone_map,
                        zone_summary: updatedExercise.zone_summary,
                        zone_map: updatedExercise.zone_map
                    });
                    
                    setSelectedExercise(updatedExercise);
                }
            } catch (err) {
                console.error('Error fetching exercise session details:', err);
                setError('Failed to load exercise session details. Please try again.');
            } finally {
                setIsLoadingDetails(false);
            }
        };
        
        fetchExerciseDetails();
    }, [patientId, selectedExercise?.originalTimestamp]);

    const handleExerciseSelect = (exercise) => {
        setSelectedExercise(exercise);
        setIsDropdownOpen(false);
    };

    const formatDateTime = (date) => {
        if (!date) return '';
        const d = typeof date === 'string' ? new Date(date) : date;
        return d.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatDuration = (minutes) => {
        if (!minutes) return '0 min';
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        if (hours > 0) {
            return `${hours}h ${mins}min`;
        }
        return `${mins}min`;
    };

    const formatDistance = (km) => {
        if (!km) return '0 km';
        return `${km.toFixed(2)} km`;
    };

    const formatSpeed = (kmh) => {
        if (!kmh) return '0 km/h';
        return `${kmh.toFixed(1)} km/h`;
    };

    // Process zone_map data for chart
    // Each entry represents a 30-second bin
    const zoneMapChartData = useMemo(() => {
        if (!selectedExercise || !selectedExercise.zone_map || !Array.isArray(selectedExercise.zone_map)) {
            return [];
        }
        
        const chartData = [];

        selectedExercise.zone_map.forEach((entry, index) => {
            // Handle zone = -1 (no data)
            const zone = entry.zone === -1 ? -1 : (entry.zone >= 0 && entry.zone <= 5 ? entry.zone : 0);

            // Each bar represents 30 seconds, so x position is in minutes
            // Bar at index 0 represents 0-30 seconds = 0-0.5 minutes (center at 0.25)
            // Bar at index 1 represents 30-60 seconds = 0.5-1 minutes (center at 0.75)
            // x represents the center of the 30-second bin in minutes
            const minutes = (index * 30 + 15) / 60; // Center of each 30-second bin
            
            // Get avg_heartrate for label
            const avgHeartrate = entry.avg_heartrate || null;

            chartData.push({
                x: minutes,
                y: zone,
                avgHeartrate: avgHeartrate
            });
        });

        return chartData;
    }, [selectedExercise]);

    // Process zone_summary data for chart
    const zoneSummaryChartData = useMemo(() => {
        if (!selectedExercise || !selectedExercise.zone_summary || typeof selectedExercise.zone_summary !== 'object') {
            return [];
        }
        
        // zone_summary format: {"0": 5, "1": 14, "2": 0.5, "-1": 10, ...}
        // Ignore zone=-1, only process zones 0-5
        const zoneCounts = [0, 0, 0, 0, 0, 0]; // Zones 0-5
        
        Object.keys(selectedExercise.zone_summary).forEach(zoneKey => {
            const zone = parseInt(zoneKey);
            const minutes = selectedExercise.zone_summary[zoneKey];
            // Ignore zone=-1 and only process valid zones 0-5
            if (zone >= 0 && zone <= 5 && typeof minutes === 'number') {
                zoneCounts[zone] = minutes;
            }
            // zone=-1 is silently ignored
        });

        // Convert to chart data format
        return zoneCounts.map((minutes, zone) => ({
            x: zone,
            y: minutes
        }));
    }, [selectedExercise]);

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="pt-safe-area-top">
                <div className="container mx-auto px-4 py-6">
                    {/* Header with Back Button and Menu */}
                    <div className="mb-4 flex items-center justify-between">
                        <button
                            onClick={() => navigate(-1)}
                            className="flex items-center text-gray-600 hover:text-blue-600 transition-colors duration-200"
                        >
                            <svg
                                className="w-5 h-5 mr-2"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 19l-7-7 7-7"
                                />
                            </svg>
                            <span className="text-sm font-medium">ফিরে যান</span>
                        </button>
                        
                        {/* Menu Dropdown */}
                        <div className="relative">
                            <button
                                onClick={() => setShowMenu(!showMenu)}
                                className="flex items-center text-gray-600 hover:text-blue-600 transition-colors duration-200 p-2"
                            >
                                <svg
                                    className="w-5 h-5"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M4 6h16M4 12h16M4 18h16"
                                    />
                                </svg>
                            </button>
                            
                            {/* Dropdown Menu */}
                            {showMenu && (
                                <>
                                    <div
                                        className="fixed inset-0 z-10"
                                        onClick={() => setShowMenu(false)}
                                    ></div>
                                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-20 border border-gray-200">
                                        <div className="py-1">
                                            <button
                                                onClick={() => {
                                                    navigate('/owner/user-data', { state: location.state });
                                                    setShowMenu(false);
                                                }}
                                                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                                            >
                                                Monitor Data
                                            </button>
                                            <button
                                                onClick={() => {
                                                    navigate('/owner/user-exercise-data', { state: location.state });
                                                    setShowMenu(false);
                                                }}
                                                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors bg-blue-50"
                                            >
                                                Exercise Data
                                            </button>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Error State */}
                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                            <div className="flex items-center">
                                <svg className="w-5 h-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                                <span className="text-red-800">{error}</span>
                            </div>
                        </div>
                    )}

                    {/* Exercise List Dropdown */}
                    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">
                            Exercise Sessions
                        </h3>
                        {isLoadingList ? (
                            <div className="flex items-center justify-center py-4">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                                <span className="ml-3 text-gray-600">Loading exercise sessions...</span>
                            </div>
                        ) : exerciseList.length > 0 ? (
                            <div className="relative">
                                <button
                                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                    className="w-full px-4 py-3 text-left bg-gray-50 border border-gray-300 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors flex items-center justify-between"
                                >
                                    <span className="text-sm text-gray-700">
                                        {selectedExercise 
                                            ? formatDateTime(selectedExercise.timestamp)
                                            : 'Select an exercise session'
                                        }
                                    </span>
                                    <svg
                                        className={`w-5 h-5 text-gray-400 transition-transform ${isDropdownOpen ? 'transform rotate-180' : ''}`}
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M19 9l-7 7-7-7"
                                        />
                                    </svg>
                                </button>
                                
                                {isDropdownOpen && (
                                    <>
                                        <div
                                            className="fixed inset-0 z-10"
                                            onClick={() => setIsDropdownOpen(false)}
                                        ></div>
                                        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                                            {exerciseList.map((exercise, index) => (
                                                <button
                                                    key={index}
                                                    onClick={() => handleExerciseSelect(exercise)}
                                                    className={`w-full px-4 py-3 text-left text-sm hover:bg-blue-50 transition-colors ${
                                                        selectedExercise?.originalTimestamp === exercise.originalTimestamp
                                                            ? 'bg-blue-100 text-blue-700'
                                                            : 'text-gray-700'
                                                    }`}
                                                >
                                                    {formatDateTime(exercise.timestamp)}
                                                </button>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </div>
                        ) : patientId ? (
                            <p className="text-gray-600">
                                No exercise data available for this patient
                            </p>
                        ) : (
                            <p className="text-gray-600">
                                No patient data available
                            </p>
                        )}
                    </div>

                    {/* Exercise Details Card */}
                    {selectedExercise && (
                        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-gray-800">
                                    Exercise Details
                                </h3>
                                {isLoadingDetails && (
                                    <div className="flex items-center">
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                        <span className="ml-2 text-sm text-gray-600">Loading...</span>
                                    </div>
                                )}
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                <div>
                                    <div className="text-xs text-gray-500 mb-1 font-bold">Timestamp</div>
                                    <div className="text-sm font-medium text-gray-800">
                                        {formatDateTime(selectedExercise.timestamp)}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-xs text-gray-500 mb-1 font-bold">Heart Rate Min</div>
                                    <div className="text-sm font-medium text-gray-800">
                                        {selectedExercise.heartrate_min} bpm
                                    </div>
                                </div>
                                <div>
                                    <div className="text-xs text-gray-500 mb-1 font-bold">Heart Rate Max</div>
                                    <div className="text-sm font-medium text-gray-800">
                                        {selectedExercise.heartrate_max} bpm
                                    </div>
                                </div>
                                <div>
                                    <div className="text-xs text-gray-500 mb-1 font-bold">Heart Rate Avg</div>
                                    <div className="text-sm font-medium text-gray-800">
                                        {selectedExercise.heartrate_avg} bpm
                                    </div>
                                </div>
                                <div>
                                    <div className="text-xs text-gray-500 mb-1 font-bold">Total Time</div>
                                    <div className="text-sm font-medium text-gray-800">
                                        {formatDuration(selectedExercise.total_time)}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-xs text-gray-500 mb-1 font-bold">Total Steps</div>
                                    <div className="text-sm font-medium text-gray-800">
                                        {selectedExercise.total_steps?.toLocaleString() || 'N/A'}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-xs text-gray-500 mb-1 font-bold">Total Distance</div>
                                    <div className="text-sm font-medium text-gray-800">
                                        {formatDistance(selectedExercise.total_distance)}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-xs text-gray-500 mb-1 font-bold">Total Calories</div>
                                    <div className="text-sm font-medium text-gray-800">
                                        {selectedExercise.total_calories || 'N/A'}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-xs text-gray-500 mb-1 font-bold">Avg Speed</div>
                                    <div className="text-sm font-medium text-gray-800">
                                        {formatSpeed(selectedExercise.avg_speed)}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-xs text-gray-500 mb-1 font-bold">VO2 Max</div>
                                    <div className="text-sm font-medium text-gray-800">
                                        {selectedExercise.vo2max} ml/kg/min
                                    </div>
                                </div>
                                <div>
                                    <div className="text-xs text-gray-500 mb-1 font-bold">Cardio Recovery Score</div>
                                    <div className="text-sm font-medium text-gray-800">
                                        {selectedExercise.cardio_recovery_score} bpm
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Heart Rate Zone Map Chart */}
                    {selectedExercise && zoneMapChartData && zoneMapChartData.length > 0 && (
                        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4">
                                Heart Rate Zone Map
                            </h3>
                            <div className="w-full overflow-x-auto">
                                <VictoryChart
                                    domainPadding={{ x: [10, 10] }}
                                    padding={{ top: 40, bottom: 50, left: 60, right: 20 }}
                                    width={Math.max(600, Math.max(zoneMapChartData.length * 40, 400))}
                                    height={250}
                                    domain={{
                                        x: zoneMapChartData.length > 0 
                                            ? [0, (zoneMapChartData.length * 30) / 60]
                                            : [0, 1],
                                        y: [-1, 5]
                                    }}
                                >
                                    <VictoryAxis
                                        label="Time (minutes)"
                                        style={{
                                            axisLabel: { padding: 35, fontSize: 12, fill: '#374151' },
                                            tickLabels: { fontSize: 10, fill: '#6B7280', angle: -45, textAnchor: 'end' }
                                        }}
                                        tickFormat={(t) => {
                                            // Format minutes with decimal precision
                                            if (t % 1 === 0) {
                                                return `${t}m`;
                                            }
                                            const mins = Math.floor(t);
                                            const secs = Math.round((t - mins) * 60);
                                            if (mins === 0) {
                                                return `${secs}s`;
                                            }
                                            return `${mins}m ${secs}s`;
                                        }}
                                    />
                                    <VictoryAxis
                                        dependentAxis
                                        label="Zone"
                                        tickValues={[-1, 0, 1, 2, 3, 4, 5]}
                                        tickFormat={(t) => t === -1 ? 'No Data' : `Zone ${t}`}
                                        domain={[-1, 5]}
                                        style={{
                                            axisLabel: { padding: 45, fontSize: 12, fill: '#374151' },
                                            tickLabels: { fontSize: 10, fill: '#6B7280' }
                                        }}
                                    />
                                    <VictoryBar
                                        data={zoneMapChartData}
                                        labels={({ datum }) => datum.avgHeartrate ? `${Math.round(datum.avgHeartrate)}` : ''}
                                        labelComponent={
                                            <VictoryLabel
                                                dy={-8}
                                                style={{ fontSize: 9, fill: '#374151', fontWeight: 'bold' }}
                                            />
                                        }
                                        style={{
                                            data: { 
                                                fill: ({ datum }) => {
                                                    const colors = [
                                                        '#9CA3AF', // Zone -1 (No Data) - Dark Gray
                                                        '#E5E7EB', // Zone 0 - Light Gray
                                                        '#3B82F6', // Zone 1 - Blue
                                                        '#10B981', // Zone 2 - Green
                                                        '#F59E0B', // Zone 3 - Orange
                                                        '#EF4444', // Zone 4 - Red
                                                        '#8B5CF6'  // Zone 5 - Purple
                                                    ];
                                                    // Map zone to color index: -1 -> 0, 0 -> 1, 1 -> 2, etc.
                                                    const colorIndex = datum.y === -1 ? 0 : datum.y + 1;
                                                    return colors[colorIndex] || '#E5E7EB';
                                                },
                                                stroke: '#374151',
                                                strokeWidth: 0.5
                                            },
                                            labels: { fontSize: 9, fill: '#374151', fontWeight: 'bold' }
                                        }}
                                        barWidth={30}
                                    />
                                </VictoryChart>
                            </div>
                        </div>
                    )}

                    {/* Zone Summary Chart */}
                    {selectedExercise && zoneSummaryChartData && zoneSummaryChartData.length > 0 && (
                        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4">
                                Zone Summary
                            </h3>
                            <div className="w-full">
                                <VictoryChart
                                    domainPadding={{ x: 20 }}
                                    padding={{ top: 60, bottom: 60, left: 60, right: 20 }}
                                    width={600}
                                    height={300}
                                    domain={{ x: [-0.2, 5.5] }}
                                >
                                    <VictoryAxis
                                        label="Zone"
                                        tickValues={[0, 1, 2, 3, 4, 5]}
                                        tickFormat={(t) => `Zone ${t}`}
                                        style={{
                                            axisLabel: { padding: 35, fontSize: 12, fill: '#374151' },
                                            tickLabels: { fontSize: 10, fill: '#6B7280' }
                                        }}
                                    />
                                    <VictoryBar
                                        data={zoneSummaryChartData}
                                        labels={({ datum }) => datum.y > 0 ? `${datum.y} min` : ''}
                                        labelComponent={
                                            <VictoryLabel
                                                dy={-5}
                                                style={{ fontSize: 11, fill: '#374151', fontWeight: 'bold' }}
                                            />
                                        }
                                        style={{
                                            data: { 
                                                fill: ({ datum }) => {
                                                    const colors = [
                                                        '#E5E7EB', // Zone 0 - Gray
                                                        '#3B82F6', // Zone 1 - Blue
                                                        '#10B981', // Zone 2 - Green
                                                        '#F59E0B', // Zone 3 - Orange
                                                        '#EF4444', // Zone 4 - Red
                                                        '#8B5CF6'  // Zone 5 - Purple
                                                    ];
                                                    return colors[datum.x] || '#E5E7EB';
                                                },
                                                stroke: '#374151',
                                                strokeWidth: 1
                                            },
                                            labels: { fontSize: 11, fill: '#374151', fontWeight: 'bold' }
                                        }}
                                        barWidth={50}
                                    />
                                </VictoryChart>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default VisualizeExerciseDataForOwner;

